import {Timer} from '@fantasmagames/core/src/update/Timer';
import {Module} from '@fantasmagames/slot/src/common/Module';
import {Views} from '@fantasmagames/slot/src/common/view/Views';
import {FreeGamesData, FreeGamesInProgress} from '@fantasmagames/slot/src/logic/integration/FreeGamesData';
import {RecodeLogic} from '@fantasmagames/slot/src/logic/integration/RecodeLogic';
import {LogicEvents} from '@fantasmagames/slot/src/logic/LogicEvents';
import {BonusGameEvents} from '@fantasmagames/slot/src/modules/bonus/BonusGameEvents';
import {FlowModuleEvents} from '@fantasmagames/slot/src/modules/flow/FlowModuleEvents';
import {FreeSpinsEvents} from '@fantasmagames/slot/src/modules/freespins/intro/FreeSpinsEvents';
import {FreeRoundsScreenEvents} from '@fantasmagames/slot/src/modules/microgaming/freerounds/FreeRoundsScreenEvents';
import {FreeRoundsScreenView} from '@fantasmagames/slot/src/modules/microgaming/freerounds/FreeRoundsScreenView';
import {FreeRoundsBasicState} from '@fantasmagames/slot/src/modules/microgaming/freerounds/states/FreeRoundsBasicState';
import {FreeRoundsInGameState} from '@fantasmagames/slot/src/modules/microgaming/freerounds/states/FreeRoundsInGameState';
import {FreeRoundsOfferScreenState} from '@fantasmagames/slot/src/modules/microgaming/freerounds/states/FreeRoundsOfferScreenState';
import {FreeRoundsResumeState} from '@fantasmagames/slot/src/modules/microgaming/freerounds/states/FreeRoundsResumeState';
import {InFreeRoundsEvents} from '@fantasmagames/slot/src/modules/microgaming/inFreeRounds/InFreeRoundsEvents';
import {ReelsEvents} from '@fantasmagames/slot/src/modules/reels/spin/ReelsEvents';
import {WinPresentationEvents} from '@fantasmagames/slot/src/modules/winpresentation/WinPresentationEvents';
import {ReelConstants} from '@fantasmagames/slot/src/modules/reels/ReelConstants';
import {GameUtils} from '@fantasmagames/slot/src/utils/GameUtils';

export interface FreeGamesTimer {
	timer: Timer;
	instanceID: string;
	offerID: string;
}

export class FreeRoundsScreenModule extends Module<FreeRoundsScreenView> {
	public static readonly BASIC_STATE: string = 'basic';
	public static readonly OFFER_STATE: string = 'offer';
	public static readonly RESUME_STATE: string = 'resume';
	public static readonly INGAME_STATE: string = 'ingame';

	protected static readonly BEFORE_SUMMARY_DELAY: number = 2000;

	protected _waitingForRestoreRoundToBeOver: boolean = false;
	protected _waitingForFreeGamesToBeOver: boolean = false;
	protected _waitingForFreeSpinsToBeOver: boolean = false;

	protected _waitingForWinPresentationToBeOver: boolean = false;

	protected _isInfoOnTheScreen: boolean = false;

	protected _freeGamesTimers: FreeGamesTimer[] = [];
	protected _canShowExpirationWindow: boolean = true;
	protected _shouldExpireCurrentFreeGames: boolean = false;
	protected _toExpireFreeGamesInstanceID: string;
	protected _toExpireFreeGamesOfferID: string;

	protected enterFirstState(): void {
		this.gotoState(FreeRoundsScreenModule.BASIC_STATE);
	}

	protected setupStates(): void {
		this.addState(new FreeRoundsBasicState(this, FreeRoundsScreenModule.BASIC_STATE));
		this.addState(new FreeRoundsOfferScreenState(this, FreeRoundsScreenModule.OFFER_STATE));
		this.addState(new FreeRoundsResumeState(this, FreeRoundsScreenModule.RESUME_STATE));
		this.addState(new FreeRoundsInGameState(this, FreeRoundsScreenModule.INGAME_STATE));
	}

	protected setupViews(): void {
		if (this.system.shouldUseMultipleViews()) {
			this.setView(Views.MICROGAMING_FREEROUNDS_VIEW_MOBILE);
		} else {
			this.setView(Views.MICROGAMING_FREEROUNDS_VIEW);
		}
	}

	protected setupEvents(): void {
		super.setupEvents();

		this.on(LogicEvents.INIT_RESPONSE, this.onInitResponse);
		this.on(LogicEvents.SPIN_RESPONSE, this.onSpinResponse);
		this.on(FlowModuleEvents.CAN_SPIN, this.onCanSpin);
		this.on(ReelsEvents.REELS_SPINNING_STARTED, this.onSpinStarted);
		this.on(WinPresentationEvents.INITIAL_ANIMATION_FINISHED, this.onInitialWinPresentationFinished);
		this.on(InFreeRoundsEvents.DISCARD_PRESSED, this.onInGameDiscardPressed);
		this.on(BonusGameEvents.BONUS_OUTRO_FINISHED, this.onBonusOutroFinished);
		this.on(FreeSpinsEvents.FREE_SPINS_OUTRO_FINISHED, this.onFreeSpinsOutroFinished);
		this.on(FreeSpinsEvents.FREE_SPINS_INTRO_STARTED, this.onFreeSpinsIntroStarted);

		this.onView(FreeRoundsScreenView.PLAY_NOW_PRESSED, this.onFreeGamesStarted);
		this.onView(FreeRoundsScreenView.PLAY_LATER_PRESSED, this.onFreeGamesPostponed);
		this.onView(FreeRoundsScreenView.DISCARD_PRESSED, this.onDiscardPressed);
		this.onView(FreeRoundsScreenView.NEXT_OFFER_PRESSED, this.onNextOfferPressed);
		this.onView(FreeRoundsScreenView.BACK_TO_GAME_PRESSED, this.onBackToGamePressed);
		this.onView(FreeRoundsScreenView.RESUME_PRESSED, this.onResumePressed);
		this.onView(FreeRoundsScreenView.DISCARD_FINAL_PRESSED, this.onDiscardFinalPressed);
		this.onView(FreeRoundsScreenView.INFO_PRESSED, this.onInfoPressed);
		this.onView(FreeRoundsScreenView.EXPIRY_CONTINUE_PRESSED, this.onExpiryContinuePressed);
		this.onViewCurrentStateHandle(FreeRoundsScreenView.KEEP_IT_PRESSED);

		this.onViewEmitEvent(FreeRoundsScreenView.SCREEN_HIDDEN, FreeRoundsScreenEvents.FREE_ROUNDS_SCREEN_HIDDEN);
		this.onViewEmitEvent(FreeRoundsScreenView.SCREEN_SHOWN, FreeRoundsScreenEvents.FREE_ROUNDS_SCREEN_SHOWN);

		if (!__PRODUCTION__) {
			document.addEventListener('keydown', this.keyboardInput.bind(this));
		}
	}

	protected keyboardInput(event: KeyboardEvent): void {
		const fgData: FreeGamesData = new FreeGamesData(null);
		fgData.setData({numChips: 5, activePaylines: 100, chipSize: 10}, 0, '123456789', 5, 5, '123', 500, 'temp1', 'temp2', '456', 456789);

		const fgInProgress: FreeGamesInProgress = new FreeGamesInProgress(null);
		fgInProgress.setData(2, 3, '123', '456', 987865413, 5, 10, 100);

		if (event.code === 'KeyQ' && event.ctrlKey) {
			this.view.showStartscreen(fgData);
		}
		if (event.code === 'KeyW' && event.ctrlKey) {
			fgData.creditType = 1;
			this.view.showStartscreen(fgData);
		}
		if (event.code === 'KeyE' && event.ctrlKey) {
			this.view.showRestoreScreen(fgInProgress, true);
		}
		if (event.code === 'KeyR' && event.ctrlKey) {
			this.view.showEndScreen(fgInProgress, 0, true);
		}
		if (event.code === 'KeyT' && event.ctrlKey) {
			this.view.showEndScreen(fgInProgress, 1, true);
		}
		if (event.code === 'KeyY' && event.ctrlKey) {
			this.view.showExpiryScreen('123', '456');
		}
		if (event.code === 'KeyU' && event.ctrlKey) {
			this.view.showDiscardQuestionsScreen(fgData);
		}
		if (event.code === 'KeyI' && event.ctrlKey) {
			this.view.showInfoText(fgData, ReelConstants.LINES > 0, ReelConstants.BOTH_WAYS);
		}
	}

	protected stopFreeGamesTimers(): void {
		for (const timer of this._freeGamesTimers) {
			timer.timer.stop();
		}
		this._freeGamesTimers = [];
	}

	protected setupFreeGamesTimers(): void {
		const logic: RecodeLogic = this.logic as RecodeLogic;

		const serverTimer: number = GameUtils.unixDateToSeconds(logic.serverTime);
		for (const freeGame of logic.freeGamesData) {
			const endTime: number = GameUtils.unixDateToSeconds(freeGame.endDate);
			const timeBeforeExpiration: number = endTime - serverTimer;
			if (timeBeforeExpiration > 0) {
				const timer: Timer = new Timer(
					() => {
						this.tryToExpireFreeGame(freeGame.instanceID, freeGame.offerID);
					},
					timeBeforeExpiration,
					true,
				);
				this._freeGamesTimers.push({timer, instanceID: freeGame.instanceID, offerID: freeGame.offerID});
				this.updateManager.add(timer);
			}
		}
	}

	protected removeFreeGamesTimer(instanceID: string, offerID: string): void {
		for (let i = 0; i < this._freeGamesTimers.length; i++) {
			if (this._freeGamesTimers[i].offerID === offerID && this._freeGamesTimers[i].instanceID === instanceID) {
				const timer: FreeGamesTimer = this._freeGamesTimers[i];
				timer.timer.stop();
				this._freeGamesTimers.splice(i, 1);

				return;
			}
		}
	}

	protected hasFreeGamesTimer(instanceID: string, offerID: string): boolean {
		for (const timer of this._freeGamesTimers) {
			if (timer.instanceID === instanceID && timer.offerID === offerID) {
				return true;
			}
		}

		return false;
	}

	protected tryToExpireFreeGame(instanceID: string, offerID: string): void {
		if (this._canShowExpirationWindow) {
			const logic: RecodeLogic = this.logic as RecodeLogic;
			let shouldSendExpiredMessage: boolean = false;
			if (this.isThisCurrentGame(instanceID, offerID)) {
				this.view.showExpiryScreen(instanceID, offerID);
				shouldSendExpiredMessage = true;
			}
			logic.recodeExpireGame(instanceID, offerID);
			this.removeFreeGamesTimer(instanceID, offerID);
			this._shouldExpireCurrentFreeGames = false;

			if (shouldSendExpiredMessage) {
				this.eventManager.emit(FreeRoundsScreenEvents.FREE_GAME_EXPIRED);
			}
		} else {
			this._shouldExpireCurrentFreeGames = true;
			this._toExpireFreeGamesInstanceID = instanceID;
			this._toExpireFreeGamesOfferID = offerID;
		}
	}

	protected isThisCurrentGame(instanceID: string, offerID: string): boolean {
		const logic: RecodeLogic = this.logic as RecodeLogic;

		return (
			(logic.currentFreeGameProgress &&
				instanceID === logic.currentFreeGameProgress.instanceID &&
				offerID === logic.currentFreeGameProgress.offerID) ||
			(logic.getCurrentFreeGamesData() &&
				instanceID === logic.getCurrentFreeGamesData().instanceID &&
				offerID === logic.getCurrentFreeGamesData().offerID)
		);
	}

	protected onResumePressed(): void {
		const logic: RecodeLogic = this.logic as RecodeLogic;
		if (logic.hasBonusGameFreeGamesRestore) {
			logic.recodeFreeGamesBonusGameRestore();
		}
		this.gotoState(FreeRoundsScreenModule.INGAME_STATE);
		this.eventManager.emit(FreeRoundsScreenEvents.FREE_GAMES_RESUMED);
		logic.startFreeGames();
	}

	protected onNextOfferPressed(): void {
		const logic: RecodeLogic = this.logic as RecodeLogic;
		if (logic.hasFreeGames()) {
			logic.finishCurrentFreeGames();
			if (this.hasFreeGamesTimer(logic.getCurrentFreeGamesData().instanceID, logic.getCurrentFreeGamesData().offerID)) {
				this.eventManager.emit(FreeRoundsScreenEvents.FREE_GAMES_NEXT_OFFER);
				this.gotoState(FreeRoundsScreenModule.OFFER_STATE);
				this.view.showStartscreen(logic.getCurrentFreeGamesData());
			}
		} else {
			throw new Error('FreeRoundsScreenModule::onNextOfferPressed no free games on next offer');
		}
	}

	protected onBackToGamePressed(): void {
		const logic: RecodeLogic = this.logic as RecodeLogic;
		logic.killAllFreeGames();
		this.gotoState(FreeRoundsScreenModule.BASIC_STATE);
		this.eventManager.emit(FreeRoundsScreenEvents.FREE_GAMES_FINISHED);
	}

	protected onFreeGamesStarted(): void {
		this.gotoState(FreeRoundsScreenModule.INGAME_STATE);
		this.startFreeGames();
	}

	protected startFreeGames(): void {
		const logic: RecodeLogic = this.logic as RecodeLogic;
		logic.startFreeGames();
		this.eventManager.emit(FreeRoundsScreenEvents.FREE_GAMES_STARTED);
	}

	protected onFreeGamesPostponed(): void {
		this.gotoState(FreeRoundsScreenModule.BASIC_STATE);
		const logic: RecodeLogic = this.logic as RecodeLogic;
		logic.postponeFreeGames();

		this.eventManager.emit(FreeRoundsScreenEvents.FREE_GAMES_POSTPONED);
		if (
			logic.hasFreeGames() &&
			this.hasFreeGamesTimer(logic.getCurrentFreeGamesData().instanceID, logic.getCurrentFreeGamesData().offerID)
		) {
			this.gotoState(FreeRoundsScreenModule.OFFER_STATE);
			this.view.showStartscreen(logic.getCurrentFreeGamesData());
		}
	}

	protected onInGameDiscardPressed(): void {
		this.onDiscardPressed();
	}

	protected onDiscardPressed(): void {
		const logic: RecodeLogic = this.logic as RecodeLogic;
		if (logic.hasFreeGames()) {
			this._isInfoOnTheScreen = false;
			this.view.showDiscardQuestionsScreen(logic.getCurrentFreeGamesData());
		} else {
			throw new Error('FreeRoundsScreenModule::onDiscardPressed no free games to discard');
		}
	}

	protected onDiscardFinalPressed(): void {
		this.discardFreeGames();

		const logic: RecodeLogic = this.logic as RecodeLogic;
		if (logic.hasBonusGameFreeGamesRestore) {
			logic.recodeFreeGamesBonusGameRestoreRejected();
		}
	}

	protected onInfoPressed(): void {
		if (this._isInfoOnTheScreen) {
			this.view.hideInfoText();
		} else {
			const logic: RecodeLogic = this.logic as RecodeLogic;
			if (logic.hasFreeGames()) {
				this.view.showInfoText(logic.getCurrentFreeGamesData(), ReelConstants.LINES > 0, ReelConstants.BOTH_WAYS);
			}
		}
		this._isInfoOnTheScreen = !this._isInfoOnTheScreen;
	}

	protected onExpiryContinuePressed(): void {
		const logic: RecodeLogic = this.logic as RecodeLogic;
		logic.clearExpiredFreeGames();

		if (logic.freeGamesData && logic.freeGamesData.length > 0) {
			this._waitingForRestoreRoundToBeOver = true;
			this.tryToShowFreeRoundsWindow();
		}
	}

	protected discardFreeGames(): void {
		const logic: RecodeLogic = this.logic as RecodeLogic;
		this.removeFreeGamesTimer(logic.getCurrentFreeGamesData().instanceID, logic.getCurrentFreeGamesData().offerID);
		logic.rejectFreeGames(null);

		this._waitingForFreeSpinsToBeOver = false;
		this._waitingForFreeGamesToBeOver = false;
		this._waitingForRestoreRoundToBeOver = false;
		this._waitingForWinPresentationToBeOver = false;

		this.eventManager.emit(FreeRoundsScreenEvents.FREE_GAMES_REJECTED);
		if (
			logic.hasFreeGames() &&
			this.hasFreeGamesTimer(logic.getCurrentFreeGamesData().instanceID, logic.getCurrentFreeGamesData().offerID)
		) {
			this.gotoState(FreeRoundsScreenModule.OFFER_STATE);
			this.view.showStartscreen(logic.getCurrentFreeGamesData());
		}
	}

	protected onInitResponse(): void {
		this.tryToSetupFreeGamesTimers();

		const logic: RecodeLogic = this.logic as RecodeLogic;

		if (logic.hasFreeGames() && logic.hasBonusGameFreeGamesRestore) {
			if (
				logic.currentFreeGameProgress ||
				logic.getCurrentFreeGamesData().gamesRemaining < logic.getCurrentFreeGamesData().gamesAwarded
			) {
				if (!logic.currentFreeGameProgress) {
					logic.makeCurrenFreeGamesDataProgress();
				}
				if (this.hasFreeGamesTimer(logic.currentFreeGameProgress.instanceID, logic.currentFreeGameProgress.offerID)) {
					this.gotoState(FreeRoundsScreenModule.RESUME_STATE);
					this.view.showRestoreScreen(logic.currentFreeGameProgress, false);
				}
			} else {
				throw new Error(
					'FreeRoundsScreenModule::onInitResponse no logic.currentFreeGameProgress on bonus game restore in free games',
				);
			}
		} else if (logic.hasFreeGames() && !logic.hasRestore) {
			if (!logic.currentFreeGameProgress) {
				logic.makeCurrenFreeGamesDataProgress();
			}
			if (logic.currentFreeGameProgress && logic.currentFreeGameProgress.gamesRemaining === 0) {
				this.showEndScreen(
					logic.currentFreeGameProgress,
					(logic.getCurrentFreeGamesData() && logic.getCurrentFreeGamesData().creditType) || -1,
					this.hasNextOffer(),
				);
			} else if (
				(logic.currentFreeGameProgress && logic.currentFreeGameProgress.gamesPlayed > 0) ||
				logic.getCurrentFreeGamesData().gamesRemaining < logic.getCurrentFreeGamesData().gamesAwarded
			) {
				if (this.hasFreeGamesTimer(logic.currentFreeGameProgress.instanceID, logic.currentFreeGameProgress.offerID)) {
					this.gotoState(FreeRoundsScreenModule.RESUME_STATE);
					this.view.showRestoreScreen(logic.currentFreeGameProgress, true);
				}
			} else {
				if (this.hasFreeGamesTimer(logic.getCurrentFreeGamesData().instanceID, logic.getCurrentFreeGamesData().offerID)) {
					this.gotoState(FreeRoundsScreenModule.OFFER_STATE);
					this.view.showStartscreen(logic.getCurrentFreeGamesData());
				}
			}
		} else if (logic.hasFreeGames() && logic.hasRestore) {
			if (!logic.currentFreeGameProgress) {
				logic.makeCurrenFreeGamesDataProgress();
			}
			if (logic.isFreeSpinsStarted) {
				if (logic.currentFreeGameProgress.gamesRemaining === 0) {
					this._waitingForFreeGamesToBeOver = true;
				}
				if (logic.currentFreeGameProgress.gamesPlayed > 0) {
					this.view.showRestoreScreen(logic.currentFreeGameProgress, false);
				} else {
					this._waitingForFreeGamesToBeOver = true;
				}
			} else {
				this._waitingForRestoreRoundToBeOver = true;
			}
		} else if (
			logic.hasExpiredFreeGames &&
			logic.currentFreeGameProgress &&
			(logic.currentFreeGameProgress.gamesRemaining > 0 || logic.isFreeSpinsStarted)
		) {
			// this.showEndScreen(logic.currentFreeGameProgress, -1, logic.freeGamesData.length > 0);
			if (logic.isFreeSpinsStarted) {
				this._waitingForFreeSpinsToBeOver = true;
				this._shouldExpireCurrentFreeGames = true;
				this._toExpireFreeGamesInstanceID = logic.currentFreeGameProgress.instanceID;
				this._toExpireFreeGamesOfferID = logic.currentFreeGameProgress.offerID;
				this.view.showRestoreScreen(logic.currentFreeGameProgress, false);
			} else {
				this.view.showExpiryScreen(logic.currentFreeGameProgress.instanceID, logic.currentFreeGameProgress.offerID);
			}
		} else if (logic.currentFreeGameProgress && logic.currentFreeGameProgress.gamesRemaining === 0) {
			if (logic.hasRestore) {
				this._waitingForFreeGamesToBeOver = true;
			} else {
				this.showEndScreen(
					logic.currentFreeGameProgress,
					(logic.getCurrentFreeGamesData() && logic.getCurrentFreeGamesData().creditType) || -1,
					false,
				);
			}
		}
	}

	protected hasNextOffer(): boolean {
		const logic: RecodeLogic = this.logic as RecodeLogic;

		if (logic.currentFreeGameProgress) {
			if (
				logic.currentFreeGameProgress.offerID === logic.getCurrentFreeGamesData().offerID &&
				logic.currentFreeGameProgress.instanceID === logic.getCurrentFreeGamesData().instanceID &&
				logic.freeGamesData.length === 1
			) {
				return false;
			}

			return true;
		}

		return logic.freeGamesData.length > 1;
	}

	protected onSpinResponse(): void {
		const logic: RecodeLogic = this.logic as RecodeLogic;
		if (logic.isInFreeRounds) {
			if (logic.currentFreeGameProgress.gamesRemaining === 0 && !logic.isFreeSpinsJustWon()) {
				this._waitingForFreeGamesToBeOver = true;
			}
		}
	}

	protected isInLastFreeSpin(): boolean {
		return this.logic.isFreeSpinsStarted === true && this.logic.freeSpinsLeft === 0;
	}

	protected tryToSetupFreeGamesTimers(): void {
		const logic: RecodeLogic = this.logic as RecodeLogic;
		this.stopFreeGamesTimers();
		if (logic.freeGamesData) {
			this.setupFreeGamesTimers();
		}
	}

	protected onBonusOutroFinished(): void {
		this._waitingForWinPresentationToBeOver = false;
		this.tryToShowFreeRoundsWindow();
	}

	protected onInitialWinPresentationFinished(): void {
		if (this._waitingForWinPresentationToBeOver && !this._waitingForFreeSpinsToBeOver) {
			this._waitingForWinPresentationToBeOver = false;
			this.tryToShowFreeRoundsWindow();
		}
	}

	protected onSpinStarted(): void {
		this._canShowExpirationWindow = false;
	}

	protected onCanSpin(): void {
		if (this.logic.isFreeSpinsStarted) {
			this._waitingForFreeSpinsToBeOver = true;
		} else if (!this._waitingForFreeSpinsToBeOver) {
			this._canShowExpirationWindow = true;
			if (this._shouldExpireCurrentFreeGames) {
				this.tryToExpireFreeGame(this._toExpireFreeGamesInstanceID, this._toExpireFreeGamesOfferID);
			}
			if (this.logic.getSpinWin() > 0 && (this._waitingForRestoreRoundToBeOver || this._waitingForFreeGamesToBeOver)) {
				this.eventManager.emit(FreeRoundsScreenEvents.EXPECTED_TO_SHOW_FREE_ROUNDS_WINDOW);
				this._waitingForWinPresentationToBeOver = true;
			} else {
				if (this._waitingForRestoreRoundToBeOver || this._waitingForFreeGamesToBeOver) {
					this.eventManager.emit(FreeRoundsScreenEvents.EXPECTED_TO_SHOW_FREE_ROUNDS_WINDOW);
				}
				this.tryToShowFreeRoundsWindow();
			}
		}
	}

	protected runAfterFreeSpinsSequence(): void {
		this._canShowExpirationWindow = true;
		if (this._shouldExpireCurrentFreeGames) {
			this.tryToExpireFreeGame(this._toExpireFreeGamesInstanceID, this._toExpireFreeGamesOfferID);
		} else {
			if (this._waitingForRestoreRoundToBeOver || this._waitingForFreeGamesToBeOver) {
				this.eventManager.emit(FreeRoundsScreenEvents.EXPECTED_TO_SHOW_FREE_ROUNDS_WINDOW);
			}
			this.tryToShowFreeRoundsWindow();
		}
	}

	protected onFreeSpinsIntroStarted(): void {
		this._waitingForFreeSpinsToBeOver = true;
	}

	protected onFreeSpinsOutroFinished(): void {
		if (this._waitingForFreeSpinsToBeOver) {
			this._waitingForFreeSpinsToBeOver = false;
			this.runAfterFreeSpinsSequence();
		}
	}

	protected showFreeRoundsWindowAccordingly(): void {
		const logic: RecodeLogic = this.logic as RecodeLogic;

		if (
			(logic.currentFreeGameProgress && logic.currentFreeGameProgress.gamesRemaining !== 0) ||
			logic.getCurrentFreeGamesData().gamesRemaining < logic.getCurrentFreeGamesData().gamesAwarded
		) {
			if (!logic.currentFreeGameProgress) {
				logic.makeCurrenFreeGamesDataProgress();
			}
			if (this.hasFreeGamesTimer(logic.currentFreeGameProgress.instanceID, logic.currentFreeGameProgress.offerID)) {
				if (logic.getCurrentFreeGamesData().gamesRemaining === logic.getCurrentFreeGamesData().gamesAwarded) {
					this.gotoState(FreeRoundsScreenModule.OFFER_STATE);
					this.view.showStartscreen(logic.getCurrentFreeGamesData());
				} else {
					this.gotoState(FreeRoundsScreenModule.RESUME_STATE);
					this.view.showRestoreScreen(logic.currentFreeGameProgress, true);
				}
			}
		} else if (logic.currentFreeGameProgress && logic.currentFreeGameProgress.gamesRemaining === 0) {
			this.startBeforeSummarySection();
		} else {
			if (this.hasFreeGamesTimer(logic.getCurrentFreeGamesData().instanceID, logic.getCurrentFreeGamesData().offerID)) {
				this.gotoState(FreeRoundsScreenModule.OFFER_STATE);
				this.view.showStartscreen(logic.getCurrentFreeGamesData());
			}
		}
	}

	protected tryToShowFreeRoundsWindow(): void {
		const logic: RecodeLogic = this.logic as RecodeLogic;
		if (this._waitingForRestoreRoundToBeOver) {
			this._waitingForRestoreRoundToBeOver = false;
			this.showFreeRoundsWindowAccordingly();
		}

		if (this._waitingForFreeGamesToBeOver) {
			this._waitingForFreeGamesToBeOver = false;
			if (logic.currentFreeGameProgress && logic.currentFreeGameProgress.gamesRemaining === 0) {
				this.startBeforeSummarySection();
			} else if (logic.currentFreeGameProgress && logic.currentFreeGameProgress.gamesPlayed === 0) {
				if (this.hasFreeGamesTimer(logic.currentFreeGameProgress.instanceID, logic.currentFreeGameProgress.offerID)) {
					this.gotoState(FreeRoundsScreenModule.OFFER_STATE);
					this.view.showStartscreen(logic.getCurrentFreeGamesData());
				}
			} else if (logic.currentFreeGameProgress) {
				this.gotoState(FreeRoundsScreenModule.RESUME_STATE);
				this.view.showRestoreScreen(logic.currentFreeGameProgress, true);
			} else {
				throw new Error('FreeRoundsScreenModule::tryToShowFreeRoundsWindow _waitingForFreeGamesToBeOver wrong data');
			}
		}
	}

	protected startBeforeSummarySection(): void {
		this.eventManager.emit(FreeRoundsScreenEvents.SOON_WILL_SHOW_FREE_ROUNDS_END);
		const logic: RecodeLogic = this.logic as RecodeLogic;
		const beforeShowingTimer: Timer = new Timer(
			() => {
				this._waitingForFreeGamesToBeOver = false;
				this.gotoState(FreeRoundsScreenModule.BASIC_STATE);

				const hasNextFreeGames: boolean = logic.hasNextFreeGames();
				this.showEndScreen(
					logic.currentFreeGameProgress,
					(logic.getCurrentFreeGamesData() && logic.getCurrentFreeGamesData().creditType) || -1,
					hasNextFreeGames,
				);
			},
			FreeRoundsScreenModule.BEFORE_SUMMARY_DELAY,
			true,
		);

		this.updateManager.add(beforeShowingTimer);
	}

	protected showEndScreen(lastFreeGame: FreeGamesInProgress, creditType: number, hasNextOffer: boolean): void {
		const logic: RecodeLogic = this.logic as RecodeLogic;

		this.view.showEndScreen(
			logic.currentFreeGameProgress,
			(logic.getCurrentFreeGamesData() && logic.getCurrentFreeGamesData().creditType) || -1,
			hasNextOffer,
		);

		this.removeFreeGamesTimer(logic.currentFreeGameProgress.instanceID, logic.currentFreeGameProgress.offerID);
	}
}



// WEBPACK FOOTER //
// ./@fantasmagames/slot/src/modules/microgaming/freerounds/FreeRoundsScreenModule.ts